import { Action, createReducer, on } from '@ngrx/store';
import { authenticateLocationSuccessAction } from '@symphony-solo/domain/location/src';
import { SoftCheckResponse } from '../models';
import {
  authenticateAction,
  authenticateErrorAction,
  authenticateSuccessAction,
  createAccountAction,
  createAccountErrorAction,
  createAccountSuccessAction,
  personalDetailsAction,
  personalDetailsErrorAction,
  personalDetailsSuccessAction,
  getAccountPropertiesAction,
  getAccountPropertiesErrorAction,
  getAccountPropertiesSuccessAction,
  getEmailDomainsAction,
  getEmailDomainsErrorAction,
  getEmailDomainsSuccessAction,
  loadAccountDetailsAction,
  loadAccountDetailsErrorAction,
  loadAccountDetailsSuccessAction,
  logoutSuccessAction,
  sendResetPasswordEmailAction,
  sendResetPasswordEmailErrorAction,
  sendResetPasswordEmailSuccessAction,
  sendValidationEmailErrorAction,
  setTokenAndRefreshTokenAction,
  softCheckAuthenticationAction,
  softCheckAuthenticationErrorAction,
  softCheckAuthenticationSuccessAction,
  changePasswordErrorAction,
  changePasswordSuccessAction,
  changePasswordAction,
  deactivateAccountAction,
  deactivateAccountSuccessAction,
  deactivateAccountErrorAction,
  tokenForValidationErrorAction,
  AccountResetPasswordAction,
  AccountResetPasswordSuccessAction,
  AccountResetPasswordErrorAction,
  setIsLoadingAction,
} from './authentication.actions';
import { AuthenticationState } from './authentication.state';

const initialState = new AuthenticationState();

const reducer = createReducer(
  initialState,
  on(authenticateAction, (state, payload) => {
    const newState = { ...state };
    newState.isLoading = true;
    newState.email = payload.credentials.email;
    return newState;
  }),
  on(authenticateSuccessAction, (state, payload) => {
    const newState = { ...state };
    newState.isLoading = false;
    newState.authToken = payload.tokenResult;
    newState.failures = null;
    return newState;
  }),
  on(authenticateErrorAction, (state, payload) => {
    const newState = { ...state };
    newState.isLoading = false;
    newState.failures = payload.failure;
    return newState;
  }),
  on(softCheckAuthenticationAction, (state, payload) => {
    const newState = { ...state };
    newState.isLoading = true;
    newState.softCheckResponse = null;
    return newState;
  }),
  on(softCheckAuthenticationSuccessAction, (state, payload) => {
    const newState = { ...state };
    newState.isLoading = false;
    newState.softCheckResponse = SoftCheckResponse.Valid;
    newState.failures = null;
    return newState;
  }),
  on(softCheckAuthenticationErrorAction, (state) => {
    const newState = { ...state };
    newState.isLoading = false;
    newState.softCheckResponse = SoftCheckResponse.Invalid;
    return newState;
  }),
  on(authenticateLocationSuccessAction, (state, payload) => {
    const newState = { ...state };
    newState.isLoading = false;
    newState.locationToken = payload.locationResponse.token;
    newState.failures = null;
    return newState;
  }),
  on(loadAccountDetailsAction, (state) => {
    const newState = { ...state };
    newState.isLoading = false;
    return newState;
  }),
  on(loadAccountDetailsSuccessAction, (state, payload) => {
    const newState = { ...state };
    newState.isLoading = false;
    newState.failures = null;
    newState.accountDetails = payload.accountDetails;
    return newState;
  }),
  on(loadAccountDetailsErrorAction, (state, payload) => {
    const newState = { ...state };
    newState.isLoading = false;
    newState.failures = payload.failure;
    return newState;
  }),
  on(setTokenAndRefreshTokenAction, (state, payload) => {
    const newState = { ...state };
    newState.isLoading = false;
    if (newState.locationToken) {
      newState.locationToken = payload.tokenInfo;
    } else if (newState.authToken) {
      newState.authToken = payload.tokenInfo;
    }
    return newState;
  }),
  on(logoutSuccessAction, (state) => {
    const newState = { ...state };
    newState.isLoading = false;
    newState.locationToken = null;
    newState.authToken = null;
    newState.accountDetails = null;
    return newState;
  }),
  on(sendValidationEmailErrorAction, (state, payload) => {
    const newState = { ...state };
    newState.isLoading = false;
    newState.failures = payload.failure;
    return newState;
  }),
  on(sendResetPasswordEmailAction, (state) => {
    const newState = { ...state };
    newState.isLoading = true;
    return newState;
  }),
  on(sendResetPasswordEmailSuccessAction, (state) => {
    const newState = { ...state };
    newState.isLoading = false;
    newState.sendResetPasswordRequest = true;
    newState.failures = null;
    return newState;
  }),
  on(sendResetPasswordEmailErrorAction, (state, payload) => {
    const newState = { ...state };
    newState.isLoading = false;
    newState.failures = payload.failure;
    newState.sendResetPasswordRequest = false;
    return newState;
  }),
  on(AccountResetPasswordAction, (state) => {
    const newState = { ...state };
    newState.isLoading = true;
    return newState;
  }),
  on(AccountResetPasswordSuccessAction, (state) => {
    const newState = { ...state };
    newState.isLoading = false;
    // newState.sendResetPasswordRequest = true;
    newState.failures = null;
    return newState;
  }),
  on(AccountResetPasswordErrorAction, (state, payload) => {
    const newState = { ...state };
    newState.isLoading = false;
    newState.failures = payload.failure;
    // newState.sendResetPasswordRequest = false;
    return newState;
  }),
  on(createAccountAction, (state) => {
    const newState = { ...state };
    newState.isLoading = true;
    return newState;
  }),
  on(createAccountSuccessAction, (state) => {
    const newState = { ...state };
    newState.isLoading = false;
    newState.createAccountRequest = null;
    newState.failures = null;
    return newState;
  }),
  on(createAccountErrorAction, (state, payload) => {
    const newState = { ...state };
    newState.isLoading = false;
    newState.failures = payload.failure;
    newState.createAccountRequest = null;
    return newState;
  }),

  on(changePasswordAction, (state) => {
    const newState = { ...state };
    newState.isLoading = true;
    return newState;
  }),
  on(changePasswordSuccessAction, (state, payload) => {
    const newState = { ...state };
    newState.isLoading = false;
    newState.changePassword = null;
    newState.changePasswordSuccess = { data: payload.responseCode };
    newState.failures = null;
    return newState;
  }),
  on(changePasswordErrorAction, (state, payload) => {
    const newState = { ...state };
    newState.isLoading = false;
    newState.failures = payload.failure;
    newState.changePassword = null;
    return newState;
  }),

  on(personalDetailsAction, (state) => {
    const newState = { ...state };
    newState.isLoading = true;
    return newState;
  }),
  on(personalDetailsSuccessAction, (state) => {
    const newState = { ...state };
    newState.isLoading = false;
    newState.personalDetails = null;
    newState.personalDetailsSuccess = { data: true };
    newState.failures = null;
    return newState;
  }),
  on(personalDetailsErrorAction, (state, payload) => {
    const newState = { ...state };
    newState.isLoading = false;
    newState.failures = payload.failure;
    newState.personalDetails = null;
    newState.personalDetailsSuccess = { data: false };
    return newState;
  }),
  on(getAccountPropertiesAction, (state) => {
    const newState = { ...state };
    newState.isLoading = true;
    return newState;
  }),
  on(getAccountPropertiesSuccessAction, (state, payload) => {
    const newState = { ...state };
    newState.isLoading = false;
    newState.accountProperties = payload.accountProperties;
    newState.failures = null;
    return newState;
  }),
  on(getAccountPropertiesErrorAction, (state, payload) => {
    const newState = { ...state };
    newState.isLoading = false;
    newState.failures = payload.failure;
    return newState;
  }),

  on(getEmailDomainsAction, (state) => {
    const newState = { ...state };
    newState.isLoading = true;
    return newState;
  }),
  on(getEmailDomainsSuccessAction, (state, payload) => {
    const newState = { ...state };
    newState.isLoading = false;
    newState.emailDomains = payload.emailDomains;
    newState.failures = null;
    return newState;
  }),
  on(getEmailDomainsErrorAction, (state, payload) => {
    const newState = { ...state };
    newState.isLoading = false;
    newState.failures = payload.failure;
    return newState;
  }),
  on(deactivateAccountAction, (state) => {
    const newState = { ...state };
    newState.isLoading = true;
    return newState;
  }),
  on(deactivateAccountSuccessAction, (state) => {
    const newState = { ...state };
    newState.isLoading = false;
    newState.deactivateAccount = { data: true };
    newState.failures = null;
    return newState;
  }),

  on(tokenForValidationErrorAction, (state) => {
    const newState = { ...state };
    newState.isLoading = false;
    return newState;
  }),
  on(deactivateAccountErrorAction, (state, payload) => {
    const newState = { ...state };
    newState.isLoading = false;
    newState.failures = payload.failure;
    return newState;
  }),
  on(setIsLoadingAction, (state, payload) => {
    const newState = { ...state };
    newState.isLoading = payload.isLoading;
    return newState;
  })
);

export function authenticationReducer(state: AuthenticationState | null, action: Action) {
  return reducer(state, action);
}
